import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import Navbaar from '../../components/Navbar/Navbaar';

import { fetchUserProfile } from '../../redux/AuthSlice/authSlice';

const PaymentSuccess = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const isCanceled = location.pathname.includes('canceled');
    const isSubscriptionFlow = location.pathname.includes('subscription');

    useEffect(() => {
        if (!isCanceled) {
            // Refresh profile after returning from Stripe so tier/credits are visible immediately.
            dispatch(fetchUserProfile());
        }
    }, [dispatch, isCanceled]);

    const content = useMemo(() => {
        if (isCanceled && isSubscriptionFlow) {
            return {
                title: 'Subscription Checkout Canceled',
                body: 'No charge was made. You can restart checkout anytime from Subscription.',
                cta: 'Back to Subscription',
                ctaLink: '/profile',
                status: 'canceled'
            };
        }

        if (isCanceled) {
            return {
                title: 'Payment Canceled',
                body: 'No charge was made. You can try again whenever you are ready.',
                cta: 'Continue Job Hunt',
                ctaLink: '/',
                status: 'canceled'
            };
        }

        if (isSubscriptionFlow) {
            return {
                title: 'Subscription Active',
                body: 'Congrats! Your subscription was successful. Your plan details are now updated.',
                cta: 'Continue Job Hunt',
                ctaLink: '/',
                status: 'success'
            };
        }

        return {
            title: 'Payment Successful',
            body: 'Your payment was successful. Credits and plan updates will appear in your account shortly.',
            cta: 'Continue Job Hunt',
            ctaLink: '/',
            status: 'success'
        };
    }, [isCanceled, isSubscriptionFlow]);

    const isSuccess = content.status === 'success';

    return (
        <div className="relative bg-[#FCFCFC] h-screen pt-16 flex flex-col">
            <div className='flex w-full'>
                <Navbaar />
            </div>

            <div className='flex flex-1 items-center justify-center w-full px-4'>
                <div className='w-full max-w-xl bg-white rounded-3xl border border-slate-200 shadow-[0_10px_40px_rgba(15,23,42,0.08)] p-6 sm:p-8'>
                    <div className="flex flex-col items-center text-center">
                        <div
                            className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                isSuccess ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                            }`}
                            aria-hidden="true"
                        >
                            {isSuccess ? (
                                <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v5m0 3h.01M22 12a10 10 0 11-20 0 10 10 0 0120 0z" />
                                </svg>
                            )}
                        </div>

                        <p className='mt-5 font-dmsans font-bold text-2xl text-slate-900'>{content.title}</p>
                        <p className='mt-2 font-dmsans font-medium text-sm sm:text-base text-slate-600 max-w-md'>{content.body}</p>

                        <div className="mt-6 w-full flex flex-col sm:flex-row gap-2.5">
                            <Link
                                className='flex-1 rounded-xl font-dmsans font-semibold text-base py-2.5 border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors'
                                to="/profile"
                            >
                                View Profile
                            </Link>
                            <Link
                                className='flex-1 rounded-xl font-dmsans font-semibold text-base py-2.5 bg-[#3F57E0] text-white hover:bg-[#344ad0] transition-colors'
                                to={content.ctaLink}
                            >
                                {content.cta}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PaymentSuccess